import React, { useEffect } from "react";

import { Controller, useForm, useFormContext } from "react-hook-form";

import { AgentVersionConfigurationInput } from "src/agents/types";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";

type ExecutionSettingsEditorProps = {
  immutable: boolean;
};

type ExecutionSettingsFormValues = {
  maxSteps: number | null;
  temperature: number | null;
};

export const ExecutionSettingsEditor: React.FC<
  ExecutionSettingsEditorProps
> = ({ immutable }) => {
  const { getValues, setValue } =
    useFormContext<AgentVersionConfigurationInput>();

  const parentValues = getValues();
  const localForm = useForm<ExecutionSettingsFormValues>({
    defaultValues: {
      maxSteps: parentValues.execution_config?.max_steps_per_session ?? null,
      temperature: parentValues.model_config?.temperature ?? null,
    },
    mode: "onChange",
  });

  // Subscribe to local form changes and propagate valid changes to parent
  useEffect(
    () =>
      localForm.subscribe({
        formState: { values: true },
        callback: async ({ values: _ }) => {
          const isValid = await localForm.trigger();
          if (!isValid) return;

          // Read current values after validation to avoid using potentially
          // stale values from when the callback was triggered
          const currentValues = localForm.getValues();
          const { maxSteps, temperature } = currentValues;
          if (maxSteps !== null) {
            setValue("execution_config.max_steps_per_session", maxSteps);
          }
          if (temperature !== null) {
            setValue("model_config.temperature", temperature);
          }
        },
      }),
    [localForm, setValue],
  );

  return (
    <div className="rounded-lg bg-gray-50 px-4 py-3">
      <FormItem
        description="The total number of cycles in the agentic loop. If the agent hits the maximum number of iterations allowed, it will stop execution."
        gap="sm"
        label="Max iterations"
        isRequired
      >
        <Controller
          control={localForm.control}
          name="maxSteps"
          render={({ field, fieldState }) => (
            <>
              <Input
                disabled={immutable}
                errored={!!fieldState.error}
                max="500"
                min="1"
                type="number"
                value={field.value ?? ""}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value === "" ? null : e.target.valueAsNumber);
                }}
              />
              {fieldState.error && (
                <ErrorHint margin="none">{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            required: "Max iterations is required",
            min: { value: 1, message: "Min value is 1" },
            max: { value: 500, message: "Max value is 500" },
          }}
        />
      </FormItem>
      <FormItem
        description="Adjust the level of randomness in the AI's output. Lower values (closer to 0) create precise and consistent responses, while higher values (closer to 1) make them more creative and varied."
        gap={false}
        label="Temperature"
        isRequired
      >
        <Controller
          control={localForm.control}
          name="temperature"
          render={({ field, fieldState }) => (
            <>
              <Input
                disabled={immutable}
                errored={!!fieldState.error}
                max="1"
                min="0"
                step="0.1"
                type="number"
                value={field.value ?? ""}
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value === "" ? null : e.target.valueAsNumber);
                }}
              />
              {fieldState.error && (
                <ErrorHint margin="none">{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            required: "Temperature is required",
            min: { value: 0, message: "Min value is 0" },
            max: { value: 1, message: "Max value is 1" },
          }}
        />
      </FormItem>
    </div>
  );
};
